import React, { useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney, convertMoneyToNumber } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconReviewStar,
  Button,
  Modal,
} from '../../components';
import InquiryForm from '../../containers/ListingPage/InquiryForm/InquiryForm';

import css from './ListingCard.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { levelsList } from '../../util/constants';
import { PRICEMULTIPLIER } from '../../util/types';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  // Convert money amount to a number and show as credits
  const credits = price ? convertMoneyToNumber(price) : null;
// If displaying in points/credits, keep one decimal place
  if (credits == null) return null;

  // Remove .0 if it's a whole number
  const formattedCredits =
    Number.isInteger(credits) ? (credits * PRICEMULTIPLIER).toString() : (credits * PRICEMULTIPLIER).toFixed(1);
  const priceCredit=(
    <span className={css.creditNumber}>{formattedCredits}</span>
  )
  return (
    <div
      className={css.price}
      title={intl.formatMessage({ id: 'ListingCard.credits' }, { credits: formattedCredits })}
    >
      {hasMultiplePriceVariants ? (
        <span className={css.statLabel}>
          <FormattedMessage
            id="ListingCard.creditsStartingFrom"
            values={{ credits: formattedCredits }}
          />
        </span>
      ) : (
        <span className={css.statLabel}>
          <FormattedMessage id="SearchPageListingCard.credits" values={{ credits: priceCredit }} />
        </span>
      )}
    </div>
  );
};
const AuthorBioSection = ({ bio }) => {
  const [expanded, setExpanded] = useState(false);
  const { text, isLong } = useMemo(() => {
    if (typeof bio !== 'string') return { text: '', isLong: false };
    const trimmed = bio.trim();
    if (trimmed.length > 140) {
      return { text: expanded ? trimmed : trimmed.slice(0, 140) + '...', isLong: true };
    }
    return { text: trimmed, isLong: false };
  }, [bio, expanded]);

  return (
    <div className={css.authorBio}>
      <div className={css.listingDescription}>
        {text}
        {isLong ? (
          <button type="button" onClick={() => setExpanded(e => !e)} className={css.seeMoreLess}>
            {expanded ? (
              <FormattedMessage id="ListingCard.showLess" />
            ) : (
              <FormattedMessage id="ListingCard.showMore" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  // Use author's profile image instead of listing's first image
  const authorFromListing = ensureUser(currentListing.author);
  const profileImage = authorFromListing?.profileImage || null;
  const variants = profileImage ? Object.keys(profileImage?.attributes?.variants || {}) : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={profileImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const [playVideo, setVideo] = useState(false);

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    reviewSummaries,
    // Optional props for Inquiry modal context
    onSubmitInquiry,
    onManageDisableScrolling,
    sendInquiryError,
    sendInquiryInProgress,
  } = props;
  const { avg, count } = reviewSummaries || {};

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const { bio } = author?.attributes?.profile || {};

  const authorName = author.attributes.profile.displayName;
  const { shortTitle, country } = author?.attributes?.profile?.publicData || {};
  const {
    listingType,
    professionalBadge,
    cardStyle,
    tagline,
    aboutMe,
    spokenLanguage = [],
    levels=[],
    ratingAvg,
    ratingCount,
videoIntroduction

  } = publicData || {};

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
    const selectedLevelKey = Array.isArray(levels) ? levels[0] : levels;
  const pageLang = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const levelItem = levelsList?.find(
    l => l.value === selectedLevelKey || l.key === selectedLevelKey
  );
  const level = levelItem
    ? pageLang === 'ar'
      ? levelItem.label_ar
      : levelItem.label_en
    : undefined;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Precompute ISO-2 country code from profile publicData.country (expects e.g. "US")
  const countryIso2 =
    (country || '').trim().length === 2 ? (country || '').trim().toUpperCase() : '';

  const ratingText = ratingAvg > 0 ? ratingAvg.toFixed(1) : '0.0';

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Inquiry modal state and handler similar to BookingFixedDurationForm
  const [isInquiryModalOpen, setInquiryModalOpen] = useState(false);
  const listingTitle = currentListing?.attributes?.title;
  const authorDisplayName = authorName;
  const inquirySubmitHandler =
    typeof onSubmitInquiry === 'function'
      ? values => {
          const maybePromise = onSubmitInquiry(values);
          // Close modal after successful submit
          return Promise.resolve(maybePromise).then(() => setInquiryModalOpen(false));
        }
      : values => {
          // eslint-disable-next-line no-console
          console.warn(
            'Inquiry submit handler missing. Please pass onSubmitInquiry prop to ListingCard.'
          );
          setInquiryModalOpen(false);
          return Promise.resolve();
        };
  // Ensure we always pass a function to Modal
  const manageDisableScrollingSafe =
    typeof onManageDisableScrolling === 'function' ? onManageDisableScrolling : () => {};
  const language = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  return (
    <>
      <NamedLink
        className={classes}
        name="ListingPage"
        params={{ id, slug }}
        onClick={e => {
          if (isInquiryModalOpen) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onMouseDown={e => {
          if (isInquiryModalOpen) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onKeyDown={e => {
          if (isInquiryModalOpen) {
            // Prevent Enter/Space from activating the link while modal is open
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }}
      >
        <div className={css.cardWrapper} {...setActivePropsMaybe}>
          <div className={css.header}>
            <div className={css.headerLeft}>
              <div className={css.avatarCol}>
                <ListingCardImage
                  renderSizes={renderSizes}
                  title={title}
                  currentListing={currentListing}
                  config={config}
                  setActivePropsMaybe={null}
                  aspectWidth={4}
                  aspectHeight={3}
                  variantPrefix={variantPrefix}
                  style={cardStyle}
                  showListingImage={showListingImage}
                />
                {/* <span className={css.onlineStatus} /> */}
                {videoIntroduction && (
                  <button
                    type="button"
                    className={css.avtarPlayIcon}
                    aria-label="Play introduction video"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setVideo(true);
                    }}
                    onMouseDown={e => {
                      // Prevent link activation on mousedown inside NamedLink
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={e => {
                      // Prevent Enter/Space from activating the link
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setVideo(true);
                      }
                    }}
                  >
                    <IconCard type="play" />
                  </button>
                )}
              </div>

              <div className={css.headerMain}>
                <div className={css.nameRow}>
                  <h3 className={css.name}>
                    {authorName}
                    {countryIso2 ? (
                      <span className={css.flagWrap}>
                        <ReactCountryFlag countryCode={countryIso2} svg aria-label={countryIso2} />
                      </span>
                    ) : null}
                  </h3>
                </div>
                <div className={css.metaRow}>
                  <div className={css.languageItem}>
                    <IconCard type="speaks" />
                    <span className={css.speaksLabel}>
                      <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />:{' '}
                    </span>
                    {/* <span className={css.languageLabel}><FormattedMessage id="ListingCard.language.arabic" /></span> */}
                    {(spokenLanguage || [])
                      .map(l =>  language =='en' ? l?.label: l?.label_ar)
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                {professionalBadge && (
                  <div className={css.badgeRow}>
                    <span className={css.badge}>
                      {publicData.badge} <FormattedMessage id="ListingCard.badge.professional" />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className={css.mobileMetaRow}>
              <div className={css.languageItem}>
                <IconCard type="speaks" />
                <span className={css.speaksLabel}>
                  <FormattedMessage id="ListingPage.speaksLabel" defaultMessage="Speaks" />:{' '}
                </span>
                {/* <span className={css.languageLabel}><FormattedMessage id="ListingCard.language.arabic" /> </span> */}
                {(spokenLanguage || [])
                  .map(l => l?.label)
                  .filter(Boolean)
                  .join(', ')}
              </div>
              {/* <div className={css.speaksLanguage}> 
               {(spokenLanguage || []).map(l => l?.label).filter(Boolean).join(', ')}
            </div> */}
            </div>

            <div className={css.actionsCol}>
              <span className={css.primaryBtn}>
                <FormattedMessage id="ListingCard.bookTrial" />
              </span>
              <Button
                type="button"
                className={css.secondaryBtn}
                onClick={e => {
                  // Prevent navigating to the ListingPage when clicking inside NamedLink
                  e.preventDefault();
                  e.stopPropagation();
                  setInquiryModalOpen(true);
                }}
              >
                <FormattedMessage id="ListingCard.sendMessage" />
              </Button>
            </div>
          </div>
          <div className={css.statsRow}>
            <div className={css.statBox}>
              <div className={css.ratingValue}>
                <FormattedMessage id="ListingCard.level" />
              </div>
              <div className={css.statValue}>{level}</div>
            </div>
            <div className={css.statBox}>
              <div className={css.statValueStrong}>
                <PriceMaybe
                  price={price}
                  publicData={publicData}
                  config={config}
                  intl={intl}
                  listingTypeConfig={foundListingTypeConfig}
                />
              </div>
              <div className={css.statValue}>
                <FormattedMessage id="ListingCard.lessonDuration" />
              </div>
            </div>
            <div className={css.statBox}>
              <div className={css.statIconRow}>
                <IconReviewStar isFilled className={css.star} label="rating" />
                <span className={css.ratingValue}>{ratingText}</span>
              </div>
              <div className={css.statValue}>
                {intl.formatMessage({ id: 'ListingCard.reviewsCount' }, { count: ratingCount || 0 })}
              </div>
            </div>
          </div>
          <div className={css.description}>
            {/* {tagline
            ? tagline
            : richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })} */}
            {bio || aboutMe ? <AuthorBioSection bio={aboutMe || bio} /> : null}
          </div>
          <div className={css.actionsColMobile}>
            <span className={css.primaryBtn}>
              <FormattedMessage id="ListingCard.bookTrial" />
            </span>
            <Button
              type="button"
              className={css.secondaryBtn}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setInquiryModalOpen(true);
              }}
            >
              <FormattedMessage id="ListingCard.sendMessage" />
            </Button>
          </div>
        </div>
      </NamedLink>
      {/* Inquiry modal rendered outside of NamedLink to avoid navigation while interacting */}
      <Modal
        id={`ListingCard.inquiry.${id}`}
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={() => setInquiryModalOpen(false)}
        usePortal
        onManageDisableScrolling={manageDisableScrollingSafe}
      >
        {isInquiryModalOpen ? (
          <InquiryForm
            className={css.inquiryForm}
            submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
            listingTitle={listingTitle}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={inquirySubmitHandler}
            inProgress={sendInquiryInProgress}
          />
        ) : null}
      </Modal>
      <Modal
        id="ListingCard.videoModal"
        isOpen={!!playVideo}
        onClose={() => setVideo(false)}
        onManageDisableScrolling={manageDisableScrollingSafe}
        usePortal
        containerClassName={css.videoModalContainer}
        contentClassName={css.videoModalContent}
        scrollLayerClassName={css.videoModalScrollLayer}
      >
        {playVideo ? (
          <>
            <div className={css.modalHeader}>
              <div className={css.modalTitle}>{ title}</div>
              <button
                type="button"
                className={css.modalClose}
                onClick={() => setVideo(false)}
                aria-label="Close"
              >
                <IconCard type="cross" />
              </button>
            </div>
            <video
              key="listing-video"
              src={videoIntroduction}
              autoPlay
              controls
              controlsList="nodownload"
              onContextMenu={e => e.preventDefault()}
            />
          </>
        ) : null}
      </Modal>
    </>
  );
};

export default ListingCard;
